import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { MapPin, Save } from 'lucide-react';

const AdminHyperlocalCategories = ({ categories = [] }) => {
  const { apiCall } = useAuth();
  const [enabledMap, setEnabledMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const fetchCategories = useCallback(async () => {
    try {
      const data = await apiCall('/api/shipping/hyperlocal/admin/categories');
      setEnabledMap(data.categories || {});
    } catch {
      // Silently handle if not loaded
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const handleToggle = (subId, enabled) => {
    setEnabledMap(prev => ({ ...prev, [subId]: enabled }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = Object.entries(enabledMap).map(([subcategory_id, hyperlocal_enabled]) => ({
        subcategory_id,
        hyperlocal_enabled,
      }));
      await apiCall('/api/shipping/hyperlocal/admin/categories', {
        method: 'PUT',
        body: JSON.stringify({ updates }),
      });
      toast.success('Hyperlocal categories updated');
      setDirty(false);
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Derive subcategory list from provided categories or just show saved ones
  const allSubs = categories.length > 0
    ? categories
    : Object.keys(enabledMap).map(id => ({ id, name: id }));

  const enabledCount = Object.values(enabledMap).filter(Boolean).length;

  return (
    <Card data-testid="admin-hyperlocal-categories">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Hyperlocal Categories
              {enabledCount > 0 && (
                <Badge className="bg-green-500 text-white text-xs">{enabledCount} enabled</Badge>
              )}
            </CardTitle>
            <CardDescription>
              Control which subcategories are eligible for hyperlocal delivery
            </CardDescription>
          </div>
          {dirty && (
            <Button size="sm" onClick={handleSave} disabled={saving} className="gap-1.5" data-testid="save-hyperlocal-categories-btn">
              <Save className="h-3.5 w-3.5" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-slate-500 py-4 text-center">Loading...</p>
        ) : allSubs.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">
            No subcategories configured yet. Toggle hyperlocal for subcategories by their ID.
          </p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {allSubs.map((sub) => {
              const subId = sub.id || sub;
              const subName = sub.name || subId;
              const enabled = enabledMap[subId] ?? false;
              return (
                <div key={subId} className="flex items-center justify-between px-3 py-2 rounded-md hover:bg-slate-50" data-testid={`hl-cat-${subId}`}>
                  <span className="text-sm text-slate-700">{subName}</span>
                  <Switch
                    checked={enabled}
                    onCheckedChange={(val) => handleToggle(subId, val)}
                  />
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminHyperlocalCategories;
